import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { toast } from "react-hot-toast";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import PdfViewerDialog from "../proofs/PdfViewerDialog";
import SmartProofPreview from "../proofs/SmartProofPreview";
import {
  getStoredEmail,
  isValidEmail,
  setStoredEmail,
} from "../proofs/proofRowUtils";
import { patchArtworkProofPackageClientApproval } from "../../services/orders";
import {
  getApprovalToast,
  getArtworkProofStatus,
  getProofRowsFromResponse,
} from "./artworkProofPackageStatus";

GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const deriveNameFromEmail = (email) => {
  const prefix = String(email || "").split("@")[0] || "";
  return prefix.trim() || "Client";
};

const isPdfFile = (fileName = "", filePath = "") => {
  const value = `${fileName || ""} ${filePath || ""}`.toLowerCase();
  return value.includes(".pdf") || value.includes("application/pdf");
};

const formatApprovalDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const getApproverLabel = (approval = {}) => {
  const by = approval?.by || {};
  return by?.name || by?.email || "";
};

const getApprovalMetadata = (label, approval = {}) => {
  if (String(approval?.state || "pending").toLowerCase() !== "approved")
    return null;
  const approver = getApproverLabel(approval);
  const approvedAt = formatApprovalDate(approval?.at);
  if (!approver && !approvedAt) return null;
  return { label, approver, approvedAt };
};

const inferRowsAfterDecision = (
  rows = [],
  state = "approved",
  approvalSide = "client",
  by = {},
) =>
  rows.map((row) => {
    const proof = row?.proof || {};
    const approvals = proof?.approvals || {};
    return {
      ...row,
      proof: {
        ...proof,
        approvals: {
          ...approvals,
          [approvalSide]: {
            ...(approvals?.[approvalSide] || {}),
            state,
            by,
            at: new Date().toISOString(),
          },
        },
      },
    };
  });

const isRowApproved = (row = {}) => {
  const proof = row?.proof || {};
  const userState = String(
    proof?.approvals?.user?.state || "pending",
  ).toLowerCase();
  const clientState = String(
    proof?.approvals?.client?.state || "pending",
  ).toLowerCase();
  const proofStatus = String(
    proof?.proofStatus || proof?.status || "",
  ).toLowerCase();

  return (
    proofStatus === "approved" ||
    (userState === "approved" && clientState === "approved")
  );
};

const isRowRejected = (row = {}) => {
  const proof = row?.proof || {};
  const userState = String(
    proof?.approvals?.user?.state || "pending",
  ).toLowerCase();
  const clientState = String(
    proof?.approvals?.client?.state || "pending",
  ).toLowerCase();
  const proofStatus = String(
    proof?.proofStatus || proof?.status || "",
  ).toLowerCase();

  return (
    proofStatus === "rejected" ||
    userState === "rejected" ||
    clientState === "rejected"
  );
};

function InlinePdfPreview({ src, fileName, onOpen }) {
  const canvasRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const loadingTaskRef = React.useRef(null);
  const renderTaskRef = React.useRef(null);
  const [loading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const cleanup = async () => {
      const renderTask = renderTaskRef.current;
      renderTaskRef.current = null;
      if (renderTask?.cancel) {
        try {
          renderTask.cancel();
        } catch {}
      }

      const loadingTask = loadingTaskRef.current;
      loadingTaskRef.current = null;
      if (loadingTask?.destroy) {
        try {
          await loadingTask.destroy();
        } catch {}
      }
    };

    const render = async () => {
      if (!src || !canvasRef.current) return;

      setLoading(true);
      setFailed(false);

      try {
        const task = getDocument({ url: src, withCredentials: false });
        loadingTaskRef.current = task;
        const pdf = await task.promise;
        if (cancelled) {
          if (pdf?.destroy) {
            try {
              await pdf.destroy();
            } catch {}
          }
          return;
        }

        const page = await pdf.getPage(1);
        const containerWidth = containerRef.current?.clientWidth || 800;
        const baseViewport = page.getViewport({ scale: 1 });
        const cssScale = Math.min(containerWidth / baseViewport.width, 1.25);
        const viewport = page.getViewport({ scale: cssScale });
        const dpr = window.devicePixelRatio || 1;

        const canvas = canvasRef.current;
        const context = canvas?.getContext("2d", { alpha: false });
        if (!canvas || !context) {
          throw new Error("Canvas context unavailable");
        }

        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);

        const renderTask = page.render({
          canvasContext: context,
          viewport,
        });
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        if (!cancelled) {
          setLoading(false);
          setFailed(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn(
            "[ArtworkProofPackagePanel] inline PDF preview failed",
            error,
          );
          setLoading(false);
          setFailed(true);
        }
      }
    };

    render();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [src]);

  if (failed) {
    return (
      <Box
        sx={{
          minHeight: 420,
          border: "1px dashed #cbd5e1",
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          backgroundColor: "#fff",
          p: 3,
        }}
      >
        <Box sx={{ display: "grid", gap: 1.25, placeItems: "center" }}>
          <PictureAsPdfOutlinedIcon color="error" sx={{ fontSize: 48 }} />
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            {fileName || "PDF proof"}
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={() => onOpen?.(src, fileName)}
          >
            Open PDF
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        minHeight: 520,
        maxHeight: 700,
        overflow: "auto",
        border: "1px dashed #cbd5e1",
        borderRadius: 2,
        backgroundColor: "#f8fafc",
        p: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: loading ? "center" : "flex-start",
        cursor: src ? "pointer" : "default",
      }}
      onClick={() => src && onOpen?.(src, fileName)}
    >
      {loading ? (
        <Typography variant="body2" sx={{ color: "#64748b" }}>
          Loading PDF preview...
        </Typography>
      ) : null}

      <canvas
        ref={canvasRef}
        style={{
          display: loading ? "none" : "block",
          maxWidth: "100%",
          height: "auto",
          background: "#fff",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
        }}
      />
    </Box>
  );
}

export default function ArtworkProofPackagePanel({
  packageGroup = null,
  proofDecisionContext = {},
  orderId = null,
  documentType = "sales-order",
  documentId = null,
  onDecisionComplete,
  embedded = false,
}) {
  const rows = Array.isArray(packageGroup?.rows) ? packageGroup.rows : [];
  const getExistingClientApprovalEmail = React.useCallback(() => {
    const proofApprovalEmail = rows
      .map((row) => row?.proof?.approvals?.client?.by?.email)
      .find((value) => isValidEmail(String(value || "").trim()));
    if (proofApprovalEmail) return String(proofApprovalEmail).trim();

    const priorSalesOrderApprovalEmail = String(
      proofDecisionContext?.priorClientApprovalEmail || ""
    ).trim();
    if (isValidEmail(priorSalesOrderApprovalEmail)) {
      return priorSalesOrderApprovalEmail;
    }

    const storedEmail = String(getStoredEmail() || "").trim();
    return isValidEmail(storedEmail) ? storedEmail : "";
  }, [proofDecisionContext?.priorClientApprovalEmail, rows]);

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [clientEmail, setClientEmail] = React.useState(() => getExistingClientApprovalEmail());
  const [comment, setComment] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [requestChangesOpen, setRequestChangesOpen] = React.useState(false);
  const [pdfOpen, setPdfOpen] = React.useState(false);
  const [pdfSrc, setPdfSrc] = React.useState("");
  const [pdfName, setPdfName] = React.useState("");

  React.useEffect(() => {
    setSelectedIndex(0);
  }, [packageGroup?.approvalGroupId]);

  React.useEffect(() => {
    if (String(clientEmail || "").trim()) return;
    const seededEmail = getExistingClientApprovalEmail();
    if (seededEmail) setClientEmail(seededEmail);
  }, [
    clientEmail,
    getExistingClientApprovalEmail,
    packageGroup?.approvalGroupId,
  ]);
  const openPdf = React.useCallback((nextSrc, nextName) => {
    setPdfSrc(nextSrc || "");
    setPdfName(nextName || "");
    setPdfOpen(true);
  }, []);

  if (!packageGroup || !rows.length) return null;

  const selectedRow = rows[selectedIndex] || rows[0];
  const selectedProof = selectedRow?.proof || {};
  const selectedStatus = getArtworkProofStatus(selectedRow);
  const selectedFilePath =
    selectedProof?.filePath ||
    selectedProof?.proofFilePath ||
    selectedRow?.mapping?.sourceFilePath ||
    "";
  const selectedFileName =
    selectedProof?.fileName ||
    selectedRow?.mapping?.sourceFileName ||
    `Proof ${selectedIndex + 1}`;
  const selectedIsPdf = isPdfFile(selectedFileName, selectedFilePath);
  const getStatusTooltip = (status = {}) => {
    const label = String(status?.label || "").toLowerCase();
    if (label === "pending review") {
      return "This proof needs approval from both your sales representative and you before it is complete.";
    }
    if (label === "client approved") {
      return "Your approval is complete. We’re just waiting for your sales representative to approve the proof.";
    }
    if (label === "internal approved") {
      return "Your sales representative has approved the proof. Your approval is still needed.";
    }
    if (label === "approved") {
      return "This proof has been approved by both your sales representative and you.";
    }
    if (label === "changes requested") {
      return "Changes have been requested for this proof.";
    }
    return status?.description || "";
  };

  const selectedApprovalMetadata = [
    getApprovalMetadata("Client approval", selectedProof?.approvals?.client),
    getApprovalMetadata("Internal approval", selectedProof?.approvals?.user),
  ].filter(Boolean);
  const isInternal = proofDecisionContext?.isInternal === true;
  const allRowsApproved = rows.length > 0 && rows.every(isRowApproved);
  const anyRowRejected = rows.some(isRowRejected);
  const packageLocked = allRowsApproved || anyRowRejected;
  const packageLockedMessage = allRowsApproved
    ? "This artwork proof package has already been approved. No further action is required."
    : "Changes have already been requested for this artwork proof package. No further action is available until the proof package is updated.";
  const hasMultipleProofs = rows.length > 1;

  const submitDecision = async (state) => {
    if (packageLocked) return;

    const trimmedComment = String(comment || "").trim();
    if (state === "rejected" && !trimmedComment) {
      toast.error("Please add a comment before requesting changes.");
      return;
    }

    const nextClientEmail = String(clientEmail || "").trim();
    if (!isInternal && !isValidEmail(nextClientEmail)) {
      toast.error("Please enter a valid email to record this proof decision.");
      return;
    }

    try {
      setSubmitting(true);
      const decisionBy = isInternal
        ? {
            name: proofDecisionContext?.internalName || "Internal User",
            email: proofDecisionContext?.internalEmail || "",
          }
        : {
            name: deriveNameFromEmail(nextClientEmail),
            email: nextClientEmail,
          };
      const decisionActor = isInternal
        ? {
            type: "internal",
            email: proofDecisionContext?.internalEmail || "",
          }
        : {
            type: "client",
            email: nextClientEmail,
          };
      const response = await patchArtworkProofPackageClientApproval(
        packageGroup.approvalGroupId,
        {
          state,
          comments: trimmedComment,
          documentType,
          documentId,
          orderId,
          ...(packageGroup?.sourceType
            ? { sourceType: packageGroup.sourceType }
            : {}),
          by: decisionBy,
          actor: decisionActor,
        },
      );
      if (!isInternal && isValidEmail(nextClientEmail)) {
        setStoredEmail(nextClientEmail);
      }
      const refreshedRows = getProofRowsFromResponse(response);
      const resultingRows = refreshedRows.length
        ? refreshedRows
        : inferRowsAfterDecision(
            rows,
            state,
            isInternal ? "user" : "client",
            decisionBy,
          );
      setComment("");
      setRequestChangesOpen(false);
      toast.success(getApprovalToast(resultingRows, state));
      onDecisionComplete?.(response);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to save the artwork proof package decision.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box
        id={`artwork-proof-package-${packageGroup.approvalGroupId}`}
        sx={{
          border: embedded ? "none" : "1px solid #d7dee3",
          borderRadius: embedded ? 0 : 2,
          p: embedded ? 0 : 2,
          mb: embedded ? 0 : 2,
          backgroundColor: embedded ? "transparent" : "#f8fbfc",
        }}
      >
        {embedded && hasMultipleProofs ? (
          <Box
            sx={{
              mb: 2,
              p: { xs: 1.5, sm: 1.75 },
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  backgroundColor: "#eef4fb",
                  color: "#46627f",
                }}
              >
                <CollectionsOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, color: "#172033", lineHeight: 1.25 }}>
                  {rows.length === 1 ? "1 artwork proof to review" : `${rows.length} artwork proofs to review`}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                  Review {rows.length === 1 ? "the proof" : "each proof"}, then approve the complete artwork package or request changes.
                </Typography>
              </Box>
            </Box>
            <Chip
              size="small"
              color={allRowsApproved ? "success" : anyRowRejected ? "warning" : "default"}
              label={allRowsApproved ? "Approved" : anyRowRejected ? "Changes requested" : "Awaiting your review"}
              sx={{ fontWeight: 700 }}
            />
          </Box>
        ) : !embedded ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Complete Artwork Proof Package
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Review all attached proof files, then approve the package once or
              request changes for the whole package.
            </Typography>
          </>
        ) : null}
        {packageLocked ? (
          <Alert
            severity={allRowsApproved ? "success" : "warning"}
            sx={{ mb: 2 }}
          >
            {packageLockedMessage}
          </Alert>
        ) : null}

        <Grid container spacing={embedded ? 1.5 : 2}>
          {hasMultipleProofs ? (
          <Grid item xs={12} md={embedded ? 3.5 : 4}>
            <List
              sx={{
                border: "1px solid #d7dee3",
                borderRadius: 2,
                backgroundColor: "#fff",
              }}
            >
              {rows.map((row, index) => {
                const status = getArtworkProofStatus(row);
                return (
                  <ListItemButton
                    key={`${row?.mapping?.id || "proof"}-${index}`}
                    selected={selectedIndex === index}
                    onClick={() => setSelectedIndex(index)}
                    divider={index < rows.length - 1}
                  >
                    <ListItemText
                      primary={
                        row?.mapping?.sourceFileName ||
                        row?.proof?.fileName ||
                        `Proof ${index + 1}`
                      }
                      secondary={[
                        `File ${index + 1} of ${rows.length}`,
                        status.description,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    />
                    <Tooltip title={getStatusTooltip(status)} arrow>
                      <Chip
                        size="small"
                        color={status.color}
                        label={status.label}
                        sx={{ cursor: "help" }}
                      />
                    </Tooltip>
                  </ListItemButton>
                );
              })}
            </List>
          </Grid>
          ) : null}

          <Grid item xs={12} md={hasMultipleProofs ? (embedded ? 8.5 : 8) : 12}>
            <Box
              sx={{
                border: embedded ? "1px solid #dfe7ef" : "1px solid #d7dee3",
                borderRadius: embedded
                  ? (hasMultipleProofs ? 2.5 : "10px 10px 0 0")
                  : 2,
                p: embedded ? { xs: 1.5, sm: 2 } : 2,
                backgroundColor: "#fff",
                boxShadow: embedded ? "0 8px 24px rgba(15, 23, 42, 0.045)" : "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {selectedFileName}
                </Typography>
                <Box sx={{ display: "grid", justifyItems: "end", gap: 0.5 }}>
                  <Tooltip title={getStatusTooltip(selectedStatus)} arrow>
                    <Chip
                      size="small"
                      color={selectedStatus.color}
                      label={selectedStatus.label}
                      sx={{ cursor: "help" }}
                    />
                  </Tooltip>
                  {selectedStatus.description ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {selectedStatus.description}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              {selectedApprovalMetadata.length ? (
                <Box sx={{ mb: 1.5, display: "grid", gap: 0.5 }}>
                  {selectedApprovalMetadata.map((metadata) => (
                    <Typography
                      key={metadata.label}
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      {metadata.label}
                      {metadata.approver ? ` by ${metadata.approver}` : ""}
                      {metadata.approvedAt ? ` on ${metadata.approvedAt}` : ""}
                    </Typography>
                  ))}
                </Box>
              ) : null}

              {selectedFilePath ? (
                selectedIsPdf ? (
                  <InlinePdfPreview
                    src={selectedFilePath}
                    fileName={selectedFileName}
                    onOpen={(filePath, fileName) => {
                      openPdf(
                        filePath || selectedFilePath,
                        fileName || selectedFileName,
                      );
                    }}
                  />
                ) : (
                  <SmartProofPreview
                    filePath={selectedFilePath}
                    fileName={selectedFileName}
                    fitContainer={embedded}
                    previewImageUrl={selectedProof?.previewImageUrl || selectedProof?.previewUrl}
                    previewStatus={selectedProof?.previewStatus}
                    previewKind={selectedProof?.previewKind}
                    onPreviewOpen={(filePath, fileName) => {
                      openPdf(
                        filePath || selectedFilePath,
                        fileName || selectedFileName,
                      );
                    }}
                    sx={{
                      width: "100%",
                      minHeight: 520,
                      maxHeight: 700,
                      border: "1px dashed #cbd5e1",
                      borderRadius: 2,
                      overflow: "auto",
                      backgroundColor: "#fff",
                    }}
                  />
                )
              ) : (
                <Alert severity="warning">
                  Preview unavailable for this proof file.
                </Alert>
              )}

              {selectedFilePath ? (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 1.5 }}
                >
                  <a
                    href={selectedFilePath}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open file in a new tab
                  </a>
                </Typography>
              ) : null}
            </Box>
          </Grid>
        </Grid>

        {!packageLocked ? (
          <Box
            sx={{
              mt: embedded ? (hasMultipleProofs ? 1.5 : "-1px") : 2,
              p: embedded ? { xs: 1.75, sm: 2 } : 0,
              display: "grid",
              gridTemplateColumns: embedded ? { xs: "1fr", md: "minmax(0, 1fr) auto" } : "1fr",
              gap: embedded ? 1.5 : 1.5,
              alignItems: "center",
              border: embedded ? "1px solid #dfe7ef" : "none",
              borderTop: embedded && !hasMultipleProofs ? "none" : undefined,
              borderRadius: embedded
                ? (hasMultipleProofs ? 2.5 : "0 0 10px 10px")
                : 0,
              backgroundColor: embedded ? "#fff" : "transparent",
              boxShadow: embedded ? "0 8px 24px rgba(15, 23, 42, 0.04)" : "none",
            }}
          >
            <Box sx={{ display: "grid", gap: 1 }}>
              {embedded ? (
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#172033" }}>
                    {hasMultipleProofs ? "Ready to continue?" : "Does this artwork look correct?"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                    {hasMultipleProofs
                      ? "Approve the artwork package if everything looks correct, or request changes and tell us what needs updating."
                      : "Approve this proof to continue, or request changes if anything needs to be updated."}
                  </Typography>
                </Box>
              ) : null}
              {!isInternal ? (
                <TextField
                  label="Your email"
                  size="small"
                  value={clientEmail}
                  onChange={(event) => setClientEmail(event.target.value)}
                  sx={{ maxWidth: embedded ? 420 : "none" }}
                />
              ) : null}
              {!embedded ? (
                <TextField
                  label="Comment for change request"
                  size="small"
                  multiline
                  minRows={2}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  helperText="Required only when requesting changes."
                />
              ) : null}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: embedded ? { xs: "stretch", sm: "flex-end" } : "flex-start",
                "& .MuiButton-root": embedded
                  ? { minHeight: 46, px: 2.25, borderRadius: 2, textTransform: "none", fontWeight: 800 }
                  : {},
              }}
            >
              <Button
                variant="contained"
                startIcon={embedded ? <CheckCircleOutlineIcon /> : undefined}
                onClick={() => {
                  submitDecision("approved");
                }}
                disabled={submitting}
                sx={embedded ? {
                  flex: { xs: "1 1 100%", sm: "0 0 auto" },
                  background: "linear-gradient(180deg, #2f9955 0%, #238244 100%)",
                  boxShadow: "0 5px 12px rgba(35, 130, 68, 0.18)",
                  "&:hover": {
                    background: "linear-gradient(180deg, #2b8f50 0%, #1f763e 100%)",
                    boxShadow: "0 7px 14px rgba(35, 130, 68, 0.22)",
                  },
                } : undefined}
              >
                {embedded
                  ? (hasMultipleProofs ? "Approve artwork package" : "Approve artwork proof")
                  : "Approve all artwork proofs"}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={embedded ? <EditNoteOutlinedIcon /> : undefined}
                onClick={() => {
                  setRequestChangesOpen(true);
                }}
                disabled={submitting}
                sx={embedded ? {
                  flex: { xs: "1 1 100%", sm: "0 0 auto" },
                  color: "#5b3a12",
                  borderColor: "#e9b54a",
                  backgroundColor: "#fffaf0",
                  "&:hover": {
                    borderColor: "#d89b22",
                    backgroundColor: "#fff4dc",
                  },
                } : undefined}
              >
                Request changes
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>

      <Dialog
        open={!packageLocked && requestChangesOpen}
        onClose={() => (submitting ? null : setRequestChangesOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Request artwork changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Tell us what should be changed. Your comments apply to the complete artwork proof package.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="What needs to change?"
            fullWidth
            multiline
            minRows={4}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            required
            helperText="Required to send a change request."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRequestChangesOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => submitDecision("rejected")}
            disabled={submitting || packageLocked || !String(comment || "").trim()}
          >
            Send change request
          </Button>
        </DialogActions>
      </Dialog>

      <PdfViewerDialog
        open={pdfOpen}
        src={pdfSrc}
        fileName={pdfName}
        title={pdfName || "Artwork Proof"}
        onClose={() => setPdfOpen(false)}
      />
    </>
  );
}
